import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useUpdateCustomer } from "@/domains/customers/services/account-customers";
import { Customer, CustomerFormData } from "@/shared/types/customerTypes";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { Input } from "@/shared/ui/input";
import { Checkbox } from "@/shared/ui/checkbox";
import { Organization } from "@/shared/types/userTypes";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { useUserOrganizations } from "@/domains/users/services/account-users";

const defaultFormData: CustomerFormData = {
  name: '',
  address: '',
  identificationNumber: '',
  kycVerified: false,
  isSelfServiceAllowed: false,
  organizations: [],
};

interface ManageCustomerDialogProps {
  open: boolean;
  organization: Organization;
  customer: Customer;
  currentUserId: string;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const ManageCustomerDialog = ({
  open,
  organization,
  customer,
  currentUserId,
  onOpenChange,
  onSuccess,
}: ManageCustomerDialogProps) => {
  const [organizationsError, setOrganizationsError] = useState<string | null>(null);
  const updateCustomer = useUpdateCustomer();
  const [formData, setFormData] = useState<CustomerFormData>(defaultFormData);
  const { data: userOrganizations } = useUserOrganizations(open ? currentUserId : undefined);

  const hasExistingIdentificationNumber = Boolean(customer?.identificationNumber?.trim());

  const lockedOrganizationIds = useMemo(
    () => new Set((customer?.organizations ?? []).map((customerOrganization) => customerOrganization.organizationId)),
    [customer?.organizations]
  );

  const sortedUserOrganizations = useMemo(() => {
    const organizations = userOrganizations ?? [];
    const organizationById = new Map(
      organizations.map((userOrganization) => [userOrganization.organizationId, userOrganization])
    );

    for (const customerOrganization of customer?.organizations ?? []) {
      if (!organizationById.has(customerOrganization.organizationId)) {
        organizationById.set(customerOrganization.organizationId, {
          ...organization,
          organizationId: customerOrganization.organizationId,
          name: customerOrganization.name
        });
      }
    }

    return Array.from(organizationById.values()).sort((firstOrganization, secondOrganization) => {
      const firstIsLocked = lockedOrganizationIds.has(firstOrganization.organizationId);
      const secondIsLocked = lockedOrganizationIds.has(secondOrganization.organizationId);

      if (firstIsLocked && !secondIsLocked) {
        return -1;
      }
      if (!firstIsLocked && secondIsLocked) {
        return 1;
      }
      return firstOrganization.name.localeCompare(secondOrganization.name);
    });
  }, [userOrganizations, customer?.organizations, organization, lockedOrganizationIds]);

  const isLockedOrganization = (organizationId: string) => lockedOrganizationIds.has(organizationId);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormData({
      name: customer.name ?? '',
      address: customer.address ?? '',
      identificationNumber: customer.identificationNumber ?? '',
      kycVerified: customer.kycVerified ?? false,
      isSelfServiceAllowed: customer.isSelfServiceAllowed ?? false,
      organizations: (customer.organizations ?? []).map((customerOrganization) => ({
        organizationId: customerOrganization.organizationId,
      })),
    });
    setOrganizationsError(null);
  }, [open, customer]);

  const isOrganizationSelected = (organizationId: string) =>
    formData.organizations.some((selectedOrganization) => selectedOrganization.organizationId === organizationId)
    || isLockedOrganization(organizationId);

  const handleOrganizationToggle = (organizationId: string, checked: boolean) => {
    if (isLockedOrganization(organizationId) && !checked) {
      return;
    }

    setOrganizationsError(null);
    setFormData((prev) => {
      const currentOrganizations = prev.organizations;

      if (!checked) {
        return {
          ...prev,
          organizations: currentOrganizations.filter(
            (selectedOrganization) =>
              selectedOrganization.organizationId !== organizationId || isLockedOrganization(selectedOrganization.organizationId)
          ),
        };
      }

      if (currentOrganizations.some((selectedOrganization) => selectedOrganization.organizationId === organizationId)) {
        return prev;
      }

      return {
        ...prev,
        organizations: [...currentOrganizations, { organizationId }],
      };
    });
  };

  const canSubmit = useMemo(() => {
    return formData.name.trim() !== ''
      && formData.address.trim() !== ''
      && formData.identificationNumber?.trim() !== ''
      && formData.organizations.length > 0
      && (organization?.isKYCRequiredForServiceLines === true ? formData.kycVerified : true);
  }, [formData, organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.organizations.length) {
      setOrganizationsError(ERROR.CUSTOMER.SELECT_ACCOUNT);
      showError(ERROR.CUSTOMER.SELECT_ACCOUNT);
      return;
    }

    try {
      const updatePayload: CustomerFormData = {
        name: formData.name,
        address: formData.address,
        ...(!hasExistingIdentificationNumber && formData.identificationNumber
          ? { identificationNumber: formData.identificationNumber }
          : {}),
        kycVerified: formData.kycVerified,
        isSelfServiceAllowed: formData.isSelfServiceAllowed,
        organizations: formData.organizations,
      };
      await updateCustomer.mutateAsync({ customerId: customer.customerId, customerData: updatePayload });
      showSuccess(SUCCESS.CUSTOMER.UPDATED);

      onSuccess();
      onOpenChange(false);
      setFormData(defaultFormData);
    } catch (error) {
      showErrorWithApiError(error, ERROR.CUSTOMER.UPDATE);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>Manage Customer</DialogTitle>
          <DialogDescription>Manage the customer and their associated organizations.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4">

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Legal Name</Label>
              <Input
                id="name"
                name="name"
                placeholder="Enter Legal Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                placeholder="Enter Address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Identification Number */}
            <div className="space-y-2">
              <Label htmlFor="identificationNumber">Identification Number</Label>
              <Input
                id="identificationNumber"
                name="identificationNumber"
                placeholder="Enter Identification Number"
                value={formData.identificationNumber}
                onChange={handleInputChange}
                required={!hasExistingIdentificationNumber}
                disabled={hasExistingIdentificationNumber}
              />
            </div>

            {/* Select Accounts and Enable Customer Self-Service */}
            <div className="rounded-md border bg-muted/30 p-2 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Select Accounts</p>
                <p className="text-xs">{formData.organizations.length} selected</p>
              </div>
              <div className="flex flex-col gap-3">
                {organizationsError && (
                  <p className="text-xs text-destructive">{organizationsError}</p>
                )}
                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                  {sortedUserOrganizations.map((userOrganization) => {
                    const selected = isOrganizationSelected(userOrganization.organizationId);
                    const locked = isLockedOrganization(userOrganization.organizationId);
                    const accountCheckboxId = `account-${userOrganization.organizationId}`;

                    return (
                      <div
                        className="rounded-md border bg-background p-3 space-y-3"
                        key={userOrganization.organizationId}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={accountCheckboxId}
                            checked={selected}
                            disabled={locked}
                            onCheckedChange={(checked) =>
                              handleOrganizationToggle(userOrganization.organizationId, checked === true)
                            }
                          />
                          <Label
                            htmlFor={accountCheckboxId}
                            className={`flex-1 ${locked ? '' : 'cursor-pointer'}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium">{userOrganization.name}</p>
                                <p className="text-xs text-muted-foreground">ID: {userOrganization.organizationId}</p>
                              </div>
                              {locked && (
                                <Badge variant="secondary" className="shrink-0">
                                  Existing account
                                </Badge>
                              )}
                            </div>
                          </Label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Self-Service Confirmation */}
            {organization?.isSelfServiceAllowed === true && (
              <div className="rounded-md border bg-muted/30 p-4 space-y-3">
                <p className="text-sm font-medium">Self-Service Confirmation</p>
                <div className="flex gap-3 items-start items-center">
                  <Checkbox
                    id="self-service-confirmation"
                    checked={formData.isSelfServiceAllowed}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isSelfServiceAllowed: checked === true,
                      }))
                    }
                    className="mt-0.5 shrink-0"
                  />
                  <div className="space-y-2 min-w-0 flex-1">
                    <p className="text-xs leading-relaxed">
                      Allow users within this customer organization to access the portal and manage their organization's users, services, terminals, and other assigned resources.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* KYC */}
            {organization?.isKYCRequiredForServiceLines === true && (
              <div className="rounded-md border bg-muted/30 p-4 space-y-3">
                <p className="text-sm font-medium">Know Your Customer (KYC)</p>
                <div className="flex gap-3 items-start items-center">
                  <Checkbox
                    id="kyc-verified"
                    checked={formData.kycVerified}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        kycVerified: checked === true,
                      }))
                    }
                    className="mt-0.5 shrink-0"
                  />
                  <div className="space-y-2 min-w-0 flex-1">
                    <p className="text-xs leading-relaxed">
                      I confirm that all required customer due diligence has been completed and that associated records are maintained in accordance with applicable compliance obligations.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={updateCustomer.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateCustomer.isPending || !canSubmit}
              className="gap-2"
            >
              {updateCustomer.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Customer'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManageCustomerDialog;
